import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from "react-router-dom";
import { Typography } from "@mui/material";

import { SourceBook } from "@dndModels";

import API from "@utilities/smart_api";

import LoadingBox from "@components/input/LoadingBox";
import SBSectionsViewer from "@dndComponents/SBSectionsViewer";

const api = API.getInstance();

// What a source book is called in the url: its abbreviation if it has one, or its id.
export const sourceBookReadURL = (sb: SourceBook) => `/read/${sb.link_abbrev !== "" ? sb.link_abbrev : sb._id}`;

/**
 * The page for reading a source book: /read/:sb/the/page/path.
 * The rest of the url after the source book is the page being read,
 * so it can be refreshed or sent to someone as a link.
 */
export default function SourceBookRead() {
  const params = useParams();
  const sb_param = params.sb ?? "";
  const page_path = params["*"] ?? "";
  const navigate = useNavigate();
  // The source book found for sb_param (null if there's no such source book).
  const [loaded, setLoaded] = useState<{ param: string, sb: SourceBook | null } | null>(null);
  // Undefined while loading.
  const sb = loaded && loaded.param === sb_param ? loaded.sb : undefined;
  // Bumped when login is worked out, or the user's source books are, since that can change whether they can read it.
  const [, setAuthTick] = useState(0);
  const mounted = useRef(false);
  const queued_for = useRef("");

  useEffect(() => {
    mounted.current = true;
    // subscribe() keeps the first method it's given for this ref, so these only touch the stable state setter.
    api.subscription_holder.subscribe("Authenticated", mounted, () => setAuthTick(t => t + 1));
    api.subscription_holder.subscribe("My Things Loaded", mounted, () => setAuthTick(t => t + 1));
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const books = await api.getObjects<SourceBook>({ data_type: "source_book" });
      const found = books.find(o => o._id === sb_param || (o.link_abbrev !== "" && o.link_abbrev === sb_param));
      if (found) {
        await api.loadObject(found);
      }
      if (active) {
        setLoaded({ param: sb_param, sb: found ?? null });
      }
    };
    load().catch(ex => {
      console.error(ex);
      if (active) setLoaded({ param: sb_param, sb: null });
    });
    return () => { active = false; };
  }, [sb_param]);

  const auth_resolved = api.subscription_holder.auth_resolved;
  const can_read = !!sb && api.canReadSourceBook(sb);

  // Get what its special sections need loading now, so they're ready sooner when opened.
  useEffect(() => {
    if (sb && can_read && queued_for.current !== sb._id) {
      queued_for.current = sb._id;
      api.queueSpecialSectionLoads(sb);
    }
  }, [sb, can_read]);

  const onNavigate = useCallback((path: string) => {
    if (sb) {
      navigate(path === "" ? sourceBookReadURL(sb) : `${sourceBookReadURL(sb)}/${path}`);
    }
  }, [sb, navigate]);

  if (sb === undefined || !auth_resolved) {
    return <LoadingBox />;
  }
  if (sb === null) {
    return <Typography variant="body1" style={{ padding: 16 }}>That source book couldn&apos;t be found.</Typography>;
  }
  if (!can_read) {
    return (
      <div style={{ padding: 16 }}>
        <Typography variant="h5" gutterBottom>{sb.name}</Typography>
        <Typography variant="body1" gutterBottom>
          { api.user === null
            ? "You need to log in to read this source book."
            : "You don't have permission to read this source book. It isn't free, and you haven't purchased it, don't own it, and it isn't shared with you through a campaign."
          }
        </Typography>
        <Link to={sb.linkURL || `/source_book/${sb._id}`}>Go to {sb.name}</Link>
      </div>
    );
  }
  return (
    <div style={{ padding: "8px 16px" }}>
      <SBSectionsViewer
        key={sb._id}
        source_book={sb}
        sections={sb.sections}
        page_path={page_path}
        onNavigate={onNavigate}
      />
    </div>
  );
}
